import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  api,
  formatMs,
  type ModelRow,
  type ProviderView,
} from "../lib/api";
import {
  capabilityChips,
  formatContext,
  formatPrice,
  isDemoModel,
  tierLabel,
  modelMatchesFilter,
  modelMatchesQuery,
  recommendedScore,
  sortModels,
  type ModelFilterId,
  type ModelSort,
  type ModelView,
} from "../lib/model-display";
import { POLICY_META, POLICY_ORDER } from "../lib/developer";
import {
  Drawer,
  DrawerSection,
  ErrorState,
  FilterBar,
  PageHeader,
  ProviderMark,
  ScoreBar,
  SectionHeader,
  SkeletonRows,
  StatusBadge,
  toneForStatus,
} from "../components/ui";

const PILLS: Array<{ id: ModelFilterId; label: string }> = [
  { id: "all", label: "All" },
  { id: "free", label: "Free" },
  { id: "paid", label: "Paid" },
  { id: "coding", label: "Coding" },
  { id: "vision", label: "Vision" },
  { id: "tools", label: "Tools" },
  { id: "embeddings", label: "Embeddings" },
  { id: "fast", label: "Fast" },
  { id: "longctx", label: "Long context" },
  { id: "demo", label: "Demo" },
];

const CATALOG_PAGE = 30;

const SORTS: Array<{ id: ModelSort; label: string }> = [
  { id: "recommended", label: "Recommended" },
  { id: "fastest", label: "Fastest" },
  { id: "quality", label: "Best quality" },
  { id: "context", label: "Longest context" },
  { id: "cost", label: "Lowest cost" },
];

const SCORE_LABELS: Record<string, string> = {
  quality: "Quality",
  reliability: "Reliability",
  latency: "Latency",
  coding: "Coding",
  vision: "Vision",
  reasoning: "Reasoning",
  toolReliability: "Tool use",
};

export default function ModelsPage() {
  const [models, setModels] = useState<ModelRow[]>([]);
  const [providers, setProviders] = useState<ProviderView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = (searchParams.get("filter") ?? "all") as ModelFilterId;
  const providerParam = searchParams.get("provider") ?? "";
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<ModelSort>("recommended");
  const [selected, setSelected] = useState<ModelView | null>(null);

  const setFilter = (f: ModelFilterId) =>
    setSearchParams(
      (p) => {
        const next = new URLSearchParams(p);
        if (f === "all") next.delete("filter");
        else next.set("filter", f);
        return next;
      },
      { replace: true },
    );

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [m, p] = await Promise.all([
          api<{ data: ModelRow[] }>("/api/models"),
          api<{ data: ProviderView[] }>("/api/providers"),
        ]);
        if (!cancelled) {
          setModels(m.data);
          setProviders(p.data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load models");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const providerByTypeId = useMemo(
    () => new Map(providers.map((p) => [p.providerTypeId, p])),
    [providers],
  );

  const views: ModelView[] = useMemo(
    () =>
      models.map((m) => ({
        ...m,
        providerView: m.provider?.providerTypeId
          ? providerByTypeId.get(m.provider.providerTypeId)
          : undefined,
      })),
    [models, providerByTypeId],
  );

  // Production-facing default excludes demo/mock-provider models; the "Demo"
  // pill opts into them explicitly.
  const prodViews = useMemo(() => views.filter((m) => !isDemoModel(m)), [views]);

  const filtered = useMemo(() => {
    let list =
      filter === "demo"
        ? views.filter((m) => isDemoModel(m))
        : prodViews.filter((m) => modelMatchesFilter(m, filter));
    if (providerParam)
      list = list.filter((m) => m.provider?.providerTypeId === providerParam);
    list = list.filter((m) => modelMatchesQuery(m, query));
    return sortModels(list, sort);
  }, [views, prodViews, filter, providerParam, query, sort]);

  const [page, setPage] = useState(0);
  useEffect(() => {
    setPage(0);
  }, [filter, providerParam, query, sort]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / CATALOG_PAGE));
  const paged = filtered.slice(page * CATALOG_PAGE, (page + 1) * CATALOG_PAGE);

  const providerOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const m of prodViews) {
      if (m.provider) seen.set(m.provider.providerTypeId, m.provider.displayName);
    }
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [prodViews]);

  const freeCount = prodViews.filter((m) => m.pricing.tier === "free").length;

  return (
    <div className="mx-auto max-w-6xl pb-16">
      <PageHeader
        title="Models"
        subtitle="Capability explorer across free and BYOK deployments."
        right={
          <span className="text-xs text-canvas-muted">
            {prodViews.length} models · <span className="text-accent">{freeCount} free</span>
          </span>
        }
      />

      {/* ── NEXUS routing policies — recommended first ─────────── */}
      {filter === "all" && !query.trim() && !providerParam && (
        <section className="mb-5">
          <SectionHeader
            title="NEXUS routing policies"
            subtitle="Virtual models — NEXUS picks the best real deployment per request. Use these IDs in your API calls."
          />
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {POLICY_ORDER.map((id) => {
              const meta = POLICY_META[id];
              if (!meta) return null;
              return (
                <Link
                  key={id}
                  to={`/playground?model=${id}`}
                  className="panel group flex flex-col p-3.5 transition hover:border-accent/40"
                >
                  <div className="mb-1 flex items-center gap-2">
                    <span className="chip-accent !text-[10px]">POLICY</span>
                    <span className="text-sm font-medium">{meta.name}</span>
                  </div>
                  <div className="mono mb-1 text-xs text-canvas-muted">{id}</div>
                  <p className="text-xs text-canvas-muted">{meta.tagline}</p>
                  <span className="mt-2 text-[11px] text-accent opacity-0 transition group-hover:opacity-100">
                    Try in playground →
                  </span>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      <FilterBar
        query={query}
        onQuery={setQuery}
        placeholder="Search models — try “llama”, “vision”, “free”…"
        pills={PILLS.map((p) => ({
          id: p.id,
          label: p.label,
          count:
            p.id === "demo"
              ? views.filter((m) => isDemoModel(m)).length
              : p.id === "all"
                ? prodViews.length
                : prodViews.filter((m) => modelMatchesFilter(m, p.id)).length,
        }))}
        activePill={filter}
        onPill={(id) => setFilter(id as ModelFilterId)}
        sort={sort}
        onSort={(s) => setSort(s as ModelSort)}
        sortOptions={SORTS}
        right={
          <select
            value={providerParam}
            onChange={(e) =>
              setSearchParams(
                (p) => {
                  const next = new URLSearchParams(p);
                  if (e.target.value) next.set("provider", e.target.value);
                  else next.delete("provider");
                  return next;
                },
                { replace: true },
              )
            }
            className="rounded-lg border border-canvas-border bg-canvas-raised px-2.5 py-1.5 text-xs text-canvas-muted outline-none focus:border-accent"
            aria-label="Provider"
          >
            <option value="">All providers</option>
            {providerOptions.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
        }
      />

      {error && <div className="mb-4"><ErrorState message={error} /></div>}

      {loading ? (
        <SkeletonRows rows={10} height={52} />
      ) : filtered.length === 0 ? (
        <div className="panel px-6 py-10 text-center">
          <p className="mb-1 text-sm font-medium">No models match</p>
          <p className="text-sm text-canvas-muted">
            Try another search or remove filters — or connect a provider to grow the pool.
          </p>
          <Link to="/providers" className="btn-ghost mt-4 inline-flex text-xs">
            Add provider →
          </Link>
        </div>
      ) : (
        <div className="panel overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-canvas-border">
                <th className="table-th">Model</th>
                <th className="table-th">Provider</th>
                <th className="table-th">Context</th>
                <th className="table-th">Capabilities</th>
                <th className="table-th">Tier</th>
                <th className="table-th">Latency</th>
                <th className="table-th">Score</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-canvas-border">
              {paged.map((m) => (
                <tr
                  key={m.id}
                  className="cursor-pointer transition hover:bg-canvas-raised"
                  onClick={() => setSelected(m)}
                >
                  <td className="table-td">
                    <div className="font-medium">{m.displayName}</div>
                    <div className="mono text-[11px] text-canvas-muted">{m.providerModelId}</div>
                  </td>
                  <td className="table-td">
                    <div className="flex items-center gap-2">
                      {m.providerView && (
                        <ProviderMark
                          letter={m.providerView.definition.branding.letter}
                          accent={m.providerView.definition.branding.accent}
                          size={22}
                        />
                      )}
                      <div>
                        <div className="text-sm">{m.provider?.displayName ?? "—"}</div>
                        {m.providerView && (
                          <StatusBadge
                            label={m.providerView.connectionStatus.replace(/_/g, " ")}
                            tone={toneForStatus(m.providerView.connectionStatus)}
                          />
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="table-td mono">{formatContext(m.contextWindow)}</td>
                  <td className="table-td">
                    <div className="flex max-w-44 flex-wrap gap-1">
                      {capabilityChips(m.capabilities).slice(0, 4).map((c) => (
                        <span key={c} className="chip-muted !text-[10px]">{c}</span>
                      ))}
                    </div>
                  </td>
                  <td className="table-td">
                    <span className={m.pricing.tier === "free" ? "chip-ok" : "chip-muted"}>
                      {tierLabel(m)}
                    </span>
                    {m.pricing.tier !== "free" && formatPrice(m) !== "Paid" && formatPrice(m) !== "—" && (
                      <div className="mono mt-1 text-[11px] text-canvas-muted">
                        {formatPrice(m)}
                      </div>
                    )}
                  </td>
                  <td className="table-td mono text-xs text-canvas-muted">
                    {m.providerView?.health?.p50LatencyMs != null
                      ? `~${formatMs(m.providerView.health.p50LatencyMs)}`
                      : "—"}
                  </td>
                  <td className="table-td mono text-xs text-canvas-muted">
                    {recommendedScore(m).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-between border-t border-canvas-border px-4 py-2.5">
            <span className="text-xs text-canvas-muted">
              Showing {page * CATALOG_PAGE + 1}–{Math.min((page + 1) * CATALOG_PAGE, filtered.length)} of{" "}
              {filtered.length}
            </span>
            <div className="flex gap-1.5">
              <button
                className="btn-ghost !px-2.5 !py-1 text-xs disabled:opacity-40"
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
              >
                ← Prev
              </button>
              <span className="self-center text-xs text-canvas-muted">
                {page + 1} / {pageCount}
              </span>
              <button
                className="btn-ghost !px-2.5 !py-1 text-xs disabled:opacity-40"
                disabled={page >= pageCount - 1}
                onClick={() => setPage((p) => p + 1)}
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      )}

      {selected && (
        <ModelDetail
          model={selected}
          onClose={() => setSelected(null)}
          onChanged={async () => {
            const res = await api<{ data: ModelRow[] }>("/api/models");
            setModels(res.data);
          }}
        />
      )}
    </div>
  );
}

/* ── Model detail drawer ───────────────────────────────────────── */

function ModelDetail({
  model,
  onClose,
  onChanged,
}: {
  model: ModelView;
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const pv = model.providerView;
  const scores = Object.entries(model.scores).filter(([k]) => SCORE_LABELS[k]);

  return (
    <Drawer
      title={
        <span className="flex items-center gap-3">
          {pv && (
            <ProviderMark
              letter={pv.definition.branding.letter}
              accent={pv.definition.branding.accent}
              size={30}
            />
          )}
          <span className="min-w-0">
            <span className="block truncate">{model.displayName}</span>
            <span className="mono block truncate text-[11px] font-normal text-canvas-muted">
              {model.providerModelId}
            </span>
          </span>
        </span>
      }
      onClose={onClose}
      wide
    >
      {/* Deployment */}
      <DrawerSection title="Deployment">
        <div className="rounded-xl border border-canvas-border bg-canvas p-3.5">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">
              {model.provider?.displayName ?? "Unknown provider"}
            </span>
            {pv && (
              <StatusBadge
                label={pv.connectionStatus.replace(/_/g, " ")}
                tone={toneForStatus(pv.connectionStatus)}
              />
            )}
          </div>
          <p className="mt-1 text-xs text-canvas-muted">
            {pv ? (
              <>
                {pv.connectionMode === "anonymous" ? "Anonymous access" : "Authenticated"} ·{" "}
                {formatMs(pv.health?.p50LatencyMs)} p50 · {formatPctSafe(pv.health?.successRate)}
              </>
            ) : (
              "Provider details unavailable"
            )}
          </p>
          <div className="mt-2.5 flex gap-2">
            <Link
              to={`/providers?id=${model.provider?.providerTypeId ?? ""}`}
              className="btn-ghost !px-2.5 !py-1 text-xs"
            >
              Open provider →
            </Link>
            <Link
              to={`/requests?model=${encodeURIComponent(model.id)}`}
              className="btn-ghost !px-2.5 !py-1 text-xs"
            >
              View requests →
            </Link>
          </div>
        </div>
      </DrawerSection>

      {/* Access + pricing */}
      <DrawerSection title="Access & pricing">
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-lg border border-canvas-border bg-canvas px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-canvas-muted">Tier</div>
            <div className={`mt-0.5 text-sm font-medium ${model.pricing.tier === "free" ? "text-accent" : ""}`}>
              {model.pricing.tier === "free" ? "Free" : "Paid"}
            </div>
          </div>
          <div className="rounded-lg border border-canvas-border bg-canvas px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-canvas-muted">Price</div>
            <div className="mono mt-0.5 text-sm">
              {model.pricing.tier === "free"
                ? "$0"
                : model.pricing.inputPerMillion || model.pricing.outputPerMillion
                  ? formatPrice(model)
                  : "—"}
            </div>
          </div>
          <div className="rounded-lg border border-canvas-border bg-canvas px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-canvas-muted">Context</div>
            <div className="mt-0.5 text-sm font-medium">
              {model.contextWindow.toLocaleString()} tokens
            </div>
          </div>
          <div className="rounded-lg border border-canvas-border bg-canvas px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-canvas-muted">Status</div>
            <div className="mt-0.5 text-sm font-medium">
              {model.enabled ? "Enabled" : "Disabled"}
            </div>
          </div>
        </div>
      </DrawerSection>

      {/* Capabilities */}
      <DrawerSection title="Capabilities">
        <div className="flex flex-wrap gap-1.5">
          {capabilityChips(model.capabilities).map((c) => (
            <span key={c} className="chip-muted">{c}</span>
          ))}
          {capabilityChips(model.capabilities).length === 0 && (
            <span className="text-xs text-canvas-muted">No capability flags recorded</span>
          )}
        </div>
      </DrawerSection>

      {/* Scores */}
      {scores.length > 0 && (
        <DrawerSection title="Router scores">
          <div className="space-y-1.5 rounded-xl border border-canvas-border bg-canvas p-3.5">
            {scores.map(([k, v]) => (
              <ScoreBar key={k} label={SCORE_LABELS[k] ?? k} value={v} />
            ))}
            <div className="border-t border-canvas-border pt-2 text-[11px] text-canvas-muted">
              Recommendation score:{" "}
              <span className="mono text-canvas-ink">{recommendedScore(model).toFixed(2)}</span>
            </div>
          </div>
        </DrawerSection>
      )}

      <div className="flex gap-2">
        <button
          className={model.enabled ? "btn-ghost" : "btn-primary"}
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await api(`/api/models/${model.id}`, {
                method: "PATCH",
                body: JSON.stringify({ enabled: !model.enabled }),
              });
              await onChanged();
              onClose();
            } finally {
              setBusy(false);
            }
          }}
        >
          {model.enabled ? "Disable model" : "Enable model"}
        </button>
      </div>
    </Drawer>
  );
}

function formatPctSafe(n: number | null | undefined): string {
  return n == null ? "—" : `${(n * 100).toFixed(1)}%`;
}
